<template>
    <div ref="telephoneInputComponent" class="telephone-input-component">
        <div class="d-flex align-items-center">
            <div :class="['flex-grow', { 'border-error error-anchor': phoneError }]" style="border-radius: 3px">
                <vue-tel-input
                    ref="vueTelInputComponents"
                    v-model="phone"
                    :mode="mode"
                    @input="onInput"
                    @validate="onValidate"
                    @country-changed="countryChanged"
                    :validCharactersOnly="true"
                    :inputOptions="inputOptions"
                    :dropdownOptions="dropdownOptions"
                    :defaultCountry="defaultCountryInternal"
                    styleClasses="pl-0"
                    :onlyCountries="allowedCountries"
                    @blur="onBlur"
                >
                    <template slot="arrow-icon">
                        <span class="vti__dropdown-arrow" v-show="allowedCountries.length > 1">&#x25BC;</span></template
                    >
                </vue-tel-input>
                <input
                    v-model="parsedPhoneDetails.national"
                    type="hidden"
                    name="telephone_national"
                />
                <input
                    v-model="parsedPhoneDetails.international"
                    type="hidden"
                    name="telephone_international"
                />
                <input
                    v-model="parsedPhoneDetails.international_prefix"
                    type="hidden"
                    name="telephone_international_prefix"
                />
            </div>

            <button class="border-0 p-0 bg-transparent ml-3" type="button" @click="handleDeleteNumber" v-if="showClearButton">
                <svg-close-circle />
            </button>
        </div>
        <div class="invalid-feedback d-block" v-if="phoneError">
            {{ phoneError }}
        </div>
    </div>
</template>

<script>
// import Translations from "./insert-me-to-translate.vue";

const VueTelInput = () =>
    Promise.all([
        import(/* webpackChunkName: "chunk-vue-tel-input", webpackPreload: true */ "vue-tel-input"),
        import(
            /* webpackChunkName: "chunk-vue-tel-input", webpackPreload: true */ "vue-tel-input/dist/vue-tel-input.css"
        ),
    ]).then(([{ VueTelInput }]) => VueTelInput);

const getLibphonenumber = () => import(/* webpackChunkName: "chunk-libphonenumber", webpackPreload: true */ 'libphonenumber-js/max');

import get from "lodash/get";

let examples = null;
const getExamples = () =>
    new Promise((resolve) =>
        examples
            ? resolve(examples)
            : import(/* webpackChunkName: "chunk-examples-mobile", webpackPreload: true,  webpackMode: "eager" */ "libphonenumber-js/examples.mobile.json").then(
                  (results) => {
                      // console.log("----- results promise:", results);
                      examples = get(results, "default", results);  // see: https://gitlab.com/catamphetamine/libphonenumber-js/-/issues/42 compatibility with  "ES Modules" export
                      resolve(examples);
                  }
              )
    );

export default {
    components: {
        VueTelInput,
        SvgCloseCircle: () => import(/* webpackChunkName: "chunk-svg-close-circle" */ './svg/close-circle.vue'),
    },

    props: {
        phoneException: {
            type: String,
            required: false,
            default: '',
        },
        defaultCountry: {
            // Default country code, ie: 'AU'
            type: String,
            required: true,
        },
        preferredCountries: {
            type: Array,
            default: () => [],
        },
        forbiddenPrefixes: {
            type: Array,
            default: () => [],
        },
        value: {
            type: Object,
            required: true,
        },
        blockEditing: {
            type: Boolean,
            required: false,
            default: false,
        },
        allowedForeignCountries: {
            type: Array,
            default: () => [],
        },
        foreignNumberError: {
            type: String,
            required: false,
            default: '',
        },
        foreignNumberLightbox: {
            type: Boolean,
            required: false,
            default: false,
        },
        validateOnBlurEvent:{
            type: Boolean,
            required: false,
            default: false
        },
        phoneNumberRequired:{
            type: Boolean,
            required: false,
            default: true
        },
        onRegenerateWithAIButtonClick: {
            type: Function,
            required: false,
            default: () => {}
        },
        renderedInModalAIContentGeneration: {
            type: Boolean,
            required: false,
            default: false
        },
        useClearButton: {
            type: Boolean,
            default: false
        },
      validateOnInput:{
          //if this is true, the validate function will be called on input
            type: Boolean,
            required: false,
            default: true
        }
    },

    data() {
        const emptyPhoneDetails = {
            national: "",
            international: "",
            international_prefix: "",
            valid: false,
            numberType: null,
        };

        const data = {
            mode: "international",
            placeholder: "",
            phoneError: "",
            emptyPhoneDetails,
            parsedPhoneDetails: JSON.parse(JSON.stringify(emptyPhoneDetails)),
          _keydownPreventHandler: null
        };

        data.defaultCountryInternal = this.defaultCountry;
        if (data.defaultCountryInternal === "XA") {
            data.defaultCountryInternal = "IT"
        }

        data.phone = this.value.contact || "";

        return data;
    },
    watch: {
       // Watches for changes in the "blockEditing" prop.
      // When "blockEditing" changes, it calls the function to update event listeners
      // on the input field to either prevent or allow user typing.
      blockEditing() {
        this.toggleInputEditingEventListener();
      },
      // Watches for changes to the "value.contact" property.
      // When "value.contact" changes, the handler checks if the new contact value
      // differs from the current "phone" value. If so, it updates "phone" with the new value,
      // or clears it if the new value is null or undefined.
      'value.contact': {
        handler(newContact) {
          if (newContact !== this.phone) {
            this.phone = newContact || '';
          }
        }
      }
    },
    computed: {
        showClearButton() {
            return this.useClearButton && this.phone && this.phone.length > 3;
        },
        inputOptions() {
            // see: https://iamstevendao.github.io/vue-tel-input/documentation/props.html
            return {
                autocomplete: "on",
                autofocus: false,
                "aria-describedby": "",
                id: "",
                maxlength: 25,
                name: "telephone",
                placeholder: this.blockEditing ? '' : this.placeholder,
                required: false,
                tabindex: 0,
                type: "tel",
                styleClasses: { "form-control": true },
                readonly: this.blockEditing,
            };
        },

        dropdownOptions() {
            return {
                disabled: this.blockEditing || this.allowedForeignCountries.length === 0,
                showDialCodeInList: true,
                showDialCodeInSelection: false,
                showFlags: true,
                showSearchBox: false,
                tabindex: 0,
                width: "390px",
            };
        },

        allowedCountries() {
            return this.allowedForeignCountries.concat([this.defaultCountry]); // returns a new array
        },

        isMobileNumber() {
            return this.parsedPhoneDetails.numberType === 'MOBILE' || this.parsedPhoneDetails.numberType === 'FIXED_LINE_OR_MOBILE';
        },
    },
    created() {
      this._keydownPreventHandler = (evt) => {
        evt.preventDefault();
      };
    },
    mounted() {
        this.$root.$on("lightbox-insert-phone-number-value", (telephoneDetails) => {

          if (this.renderedInModalAIContentGeneration) {
            this.phone = telephoneDetails?.contact ? telephoneDetails.contact : '';
            if (this.validate()) this.$root.$emit("lightbox-insert-phone-number-validation-successful", telephoneDetails)
          }

        });

        this.phoneError = this.phoneException;
        const componentThis = this;
        // reset field when country flag is changed
        $(this.$refs.telephoneInputComponent).on("click", function (event) {
            const target = $(event.target);
            const selectedCountry = target.closest("li.vti__dropdown-item");
            if (selectedCountry.length == 0) {
                return;
            }

            // force country in component: if a country is selected when a valid number starting with 00 is in the field,
            // by default the country's number is selected, not the clicked flag
            // eg: select Australia, insert 0039 391 765 4321 (nothing happens), click on Swiss flag in dropdown, Italy is selected
            const selectedCountryCode = selectedCountry
                .find(".vti__flag")
                .attr("class")
                .split(/\s+/)[1];
            componentThis.$refs.vueTelInputComponents.choose(
                selectedCountryCode
            );

            componentThis.$nextTick().then(() => {
                // reset model value
                componentThis.deleteNumber();
            });
        });

        if(this.foreignNumberLightbox) {
            this.$store.dispatch("ui/lightbox/show", {
                title: gettext("Important notice"),
                body: this.foreignNumberError,
            });
        }

    },
    beforeDestroy() {
      const inputElement = this.$refs.vueTelInputComponents?.$el?.querySelector('input');
      if (inputElement && this._keydownPreventHandler) {
        inputElement.removeEventListener('keydown', this._keydownPreventHandler);
      }
    },
    methods: {
        focus() {
            this.$refs.vueTelInputComponents.focus();
        },
        toggleInputEditingEventListener() {
        const inputElement = this.$refs.vueTelInputComponents?.$el?.querySelector('input');

        if (!inputElement) {;
          return;
        }

        if (this.blockEditing) {
          inputElement.addEventListener('keydown', this._keydownPreventHandler);
        } else {
          inputElement.removeEventListener('keydown', this._keydownPreventHandler);
        }
      },
      onInput(formattedNumber, obj) {
            this.phone = this.phone.trim();
            // replace 00 with plus symbol, forces country dection by component
            // obj and formattedNumber are not upadted, but '+' is not a valid number as well as '00'
            if (this.phone === "00") {
                this.phone = "+";
                return;
            }

            this.onValidate(obj);
            const countryCode = obj.regionCode;

            // this emit intput, triggers a change on post-form.vue's telephoneDetails
            // which is watched and toggles whatsapp on or off
            this.$emit("input", {
                contact:
                    countryCode === this.defaultCountry
                        ? get(obj, "nationalNumber", "")
                        : get(obj, "number", ""),
                // 'national': obj.nationalNumber,
                // 'country_code': countryCode,
            });
        },

        onBlur() {
            if (!this.validateOnBlurEvent) {
                return;
            }
            this.validate();

        },

        onValidate(obj) {
            getLibphonenumber().then(({getCountryCallingCode, parsePhoneNumber}) => {
                this.parsedPhoneDetails.national = get(obj, "nationalNumber", "");
                this.parsedPhoneDetails.international = get(obj, "number", "");
                this.parsedPhoneDetails.valid = obj.valid;
                this.parsedPhoneDetails.international_prefix = "+" + obj.country.dialCode;
                if(obj.valid) {
                    this.parsedPhoneDetails.numberType = parsePhoneNumber(obj.number).getType();
                }

                for(const countryCode of this.allowedCountries) {
                    if(!obj.countryCallingCode || getCountryCallingCode(countryCode) === obj.countryCallingCode) {
                        if(this.parsedPhoneDetails.errorMessage) {
                          this.phoneError = this.parsedPhoneDetails.errorMessage = '';
                        }

                        if (this.validateOnInput) this.validate()
                        return;
                    }
                }
                this.phoneError = this.parsedPhoneDetails.errorMessage = this.foreignNumberError;
                if (this.validateOnInput) this.validate()

            })
        },
        deleteNumber() {
            this.parsedPhoneDetails = JSON.parse(JSON.stringify(this.emptyPhoneDetails));

            this.phone = "";
            this.$emit("input", {
                contact: "",
            });
        },
        validate: function () {
            this.phoneError = "";

            if(!this.phoneNumberRequired){
                return !this.phoneError;
            }

            if (
                this.parsedPhoneDetails.national ||
                this.$parent.mandatoryTelephoneNumber
            ) {
                if (
                    this.parsedPhoneDetails.errorMessage
                ) {
                    this.phoneError = this.parsedPhoneDetails.errorMessage;
                } else if (
                    this.forbiddenPrefixes.includes(
                        String(this.parsedPhoneDetails.national).substr(0, 3)
                    )
                ) {
                    this.phoneError = gettext(
                        "Please, insert a valid phone number"
                    );
                } else if (
                    this.$parent.mandatoryTelephoneNumber &&
                    !this.parsedPhoneDetails.national
                ) {
                    this.phoneError = gettext(
                        "If you want to be contacted by phone, please fill in this field"
                    );
                } else if (!this.parsedPhoneDetails.valid) {
                    this.phoneError = gettext(
                        "Please, insert a valid phone number"
                    );
                }
            }

            if (this.phone.length > 0 && this.parsedPhoneDetails.valid == undefined) {
              this.phoneError = gettext("Please, insert a valid phone number");
            }

            this.$emit("is-correct-phone-format", this.parsedPhoneDetails.valid);
            return !this.phoneError;

        },

        countryChanged(country) {
            // reset field when country is changed
            this.parsedPhoneDetails.international_prefix =
                "+" + country.dialCode;

            // update placeholder
            this.resetPlaceholder(country.iso2);
            this.phone = "";
            if (this.validateOnInput) this.validate()
        },

        resetPlaceholder(countryCode) {
            getExamples()
                .then(() => {
                    return getLibphonenumber();
                })
                .then(({getExampleNumber}) => {
                    const number = getExampleNumber(
                        countryCode.toUpperCase(),
                        examples
                    );
                    const mode =
                        !this.mode || this.mode === "auto"
                            ? "international"
                            : this.mode;
                    // console.log("----- number:", number);
                    this.placeholder = number
                        ? number.format(mode.toUpperCase())
                        : "";
                })
                .catch(err => {
                    console.error(err);
                });
        },

        handleDeleteNumber() {
            this.deleteNumber();
            this.$refs.vueTelInputComponents.resetPlaceholder()

            // set contact method as email only
            this.$emit('update:contact-method', '1');
        },
    },
};
</script>
